// "How to fix it" on a finding (#1277): the tokens `fixAdvice` reads, drawn as React nodes.
// Nothing here sets HTML: every word the scanner wrote reaches the page as text.

import type { ReactNode } from "react";

import type { Finding } from "../api/posture";
import { fixAdvice, type Block, type Inline } from "./fixAdvice";

function inline(tokens: Inline[]): ReactNode[] {
  return tokens.map((token, at) => {
    switch (token.kind) {
      case "text":
        return token.text;
      case "code":
        return <code key={at}>{token.text}</code>;
      case "strong":
        return <strong key={at}>{inline(token.children)}</strong>;
      case "em":
        return <em key={at}>{inline(token.children)}</em>;
      case "link":
        return (
          <a key={at} href={token.href} target="_blank" rel="noopener noreferrer">
            {inline(token.children)}
          </a>
        );
    }
  });
}

function blocks(content: Block[]): ReactNode[] {
  return content.map((block, at) => {
    if (block.kind === "paragraph") return <p key={at}>{inline(block.children)}</p>;
    const items = block.items.map((item, n) => <li key={n}>{inline(item)}</li>);
    return block.ordered ? <ol key={at}>{items}</ol> : <ul key={at}>{items}</ul>;
  });
}

/** The disclosure under a finding, or nothing when the scanner offers no advice for it. */
export function FindingFixAdvice({ finding }: { finding: Finding }) {
  const advice = fixAdvice(finding);
  if (advice === null) return null;
  const { command } = advice;
  return (
    <details className="fix-advice">
      <summary>How to fix it</summary>
      <div className="fix-advice__body">
        {blocks(advice.recommendation)}
        {advice.risk.length === 0 ? null : (
          <>
            <p className="fix-advice__heading">Why it matters</p>
            {blocks(advice.risk)}
          </>
        )}
        {command === null ? null : (
          <figure className="fix-advice__command">
            <figcaption className="fix-advice__label">{command.label}</figcaption>
            <pre>
              <code>
                {command.parts.map((part, at) =>
                  part.kind === "placeholder" ? (
                    <mark key={at} className="fix-advice__placeholder">{`<${part.name}>`}</mark>
                  ) : (
                    part.text
                  ),
                )}
              </code>
            </pre>
            {command.unresolved.length === 0 ? null : (
              <p className="muted">Fill in before running: {command.unresolved.join(", ")}.</p>
            )}
          </figure>
        )}
        {advice.reference === null ? null : (
          <p>
            <a href={advice.reference} target="_blank" rel="noopener noreferrer">
              Read about this check
            </a>
          </p>
        )}
      </div>
    </details>
  );
}
